import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { X, Search, KeyRound, Loader2, CheckCircle, AlertCircle, Store, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api/client'
import { useAuth } from '@/lib/auth'
import type { Vendor, VendorEvent } from '@/lib/api/types'

type TabType = 'vendors' | 'vendor_events'

type ExportStep = 'select' | 'generating' | 'exporting' | 'success' | 'error'

interface ExportLoginPdfModalProps {
  eventId: number
  vendors?: Vendor[]
  vendorEvents?: VendorEvent[]
  onClose: () => void
  /** When provided, skips selection and immediately exports for these vendor event IDs */
  autoExportVendorEventIds?: number[]
}

export function ExportLoginPdfModal({ eventId, vendors = [], vendorEvents = [], onClose, autoExportVendorEventIds }: ExportLoginPdfModalProps) {
  const { token } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('vendor_events')
  const [search, setSearch] = useState('')
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<number>>(new Set())
  const [selectedVendorEventIds, setSelectedVendorEventIds] = useState<Set<number>>(new Set())
  const [step, setStep] = useState<ExportStep>(autoExportVendorEventIds ? 'generating' : 'select')
  const [error, setError] = useState<string | null>(null)
  const [generateResult, setGenerateResult] = useState<{ created: number; skipped_existing: number } | null>(null)
  const autoExportTriggered = useRef(false)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step === 'select') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, step])

  // Build a map of vendor ID -> vendor event IDs for the "by vendor" selection mode
  const vendorToEventIds = useMemo(() => {
    const map = new Map<number, number[]>()
    for (const ve of vendorEvents) {
      const vendorId = typeof ve.vendor === 'object' ? ve.vendor.id : ve.vendor
      const existing = map.get(vendorId) || []
      existing.push(ve.id)
      map.set(vendorId, existing)
    }
    return map
  }, [vendorEvents])

  // Filtered lists based on search
  const filteredVendors = useMemo(() => {
    if (!search) return vendors
    const q = search.toLowerCase()
    return vendors.filter(v => v.name.toLowerCase().includes(q))
  }, [vendors, search])

  const filteredVendorEvents = useMemo(() => {
    if (!search) return vendorEvents
    const q = search.toLowerCase()
    return vendorEvents.filter(ve => {
      const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ''
      return (
        (ve.booth || '').toLowerCase().includes(q) ||
        vendorName.toLowerCase().includes(q)
      )
    })
  }, [vendorEvents, search])

  // Derive the final list of vendor_event_ids based on selection mode
  const getSelectedVendorEventIds = (): number[] => {
    if (activeTab === 'vendor_events') {
      return Array.from(selectedVendorEventIds)
    }
    // For vendor tab, collect all vendor event IDs for selected vendors
    const ids: number[] = []
    for (const vendorId of selectedVendorIds) {
      const eventIds = vendorToEventIds.get(vendorId)
      if (eventIds) ids.push(...eventIds)
    }
    return ids
  }

  const hasSelection = activeTab === 'vendor_events'
    ? selectedVendorEventIds.size > 0
    : selectedVendorIds.size > 0

  // Select all / deselect all
  const handleSelectAll = () => {
    if (activeTab === 'vendor_events') {
      const allIds = new Set(filteredVendorEvents.map(ve => ve.id))
      setSelectedVendorEventIds(allIds)
    } else {
      const allIds = new Set(filteredVendors.map(v => v.id))
      setSelectedVendorIds(allIds)
    }
  }

  const handleDeselectAll = () => {
    if (activeTab === 'vendor_events') {
      setSelectedVendorEventIds(new Set())
    } else {
      setSelectedVendorIds(new Set())
    }
  }

  const allSelected = activeTab === 'vendor_events'
    ? filteredVendorEvents.length > 0 && filteredVendorEvents.every(ve => selectedVendorEventIds.has(ve.id))
    : filteredVendors.length > 0 && filteredVendors.every(v => selectedVendorIds.has(v.id))

  // The main export flow: generate credentials then download PDF
  const runExport = useCallback(async (vendorEventIds: number[]) => {
    if (!token || vendorEventIds.length === 0) return

    setError(null)
    setGenerateResult(null)

    try {
      setStep('generating')
      const genResult = await apiClient.generateVendorCredentials(token, eventId, {
        vendor_event_ids: vendorEventIds,
        only_missing: true,
      })
      setGenerateResult({ created: genResult.created, skipped_existing: genResult.skipped_existing })

      setStep('exporting')
      await apiClient.exportVendorCredentialsPdf(token, eventId, vendorEventIds)

      setStep('success')
    } catch (err) {
      console.error('Export login PDF failed:', err)
      setStep('error')
      setError(err instanceof Error ? err.message : 'Export failed')
    }
  }, [token, eventId])

  const handleExport = () => {
    const vendorEventIds = getSelectedVendorEventIds()
    runExport(vendorEventIds)
  }

  // Auto-export on mount when autoExportVendorEventIds is provided
  useEffect(() => {
    if (autoExportVendorEventIds && !autoExportTriggered.current) {
      autoExportTriggered.current = true
      runExport(autoExportVendorEventIds)
    }
  }, [autoExportVendorEventIds, runExport])

  const toggleVendor = (id: number) => {
    setSelectedVendorIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleVendorEvent = (id: number) => {
    setSelectedVendorEventIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectionCount = activeTab === 'vendor_events' ? selectedVendorEventIds.size : selectedVendorIds.size
  const derivedEventCount = activeTab === 'vendors' ? getSelectedVendorEventIds().length : selectedVendorEventIds.size

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={() => step === 'select' && onClose()}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              Export Login PDF
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Processing / Success / Error states */}
            {step === 'generating' && (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Generating credentials...</p>
              </div>
            )}

            {step === 'exporting' && (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <div className="text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Downloading PDF...</p>
                  {generateResult && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                      {generateResult.created > 0 && `${generateResult.created} new credential(s) created. `}
                      {generateResult.skipped_existing > 0 && `${generateResult.skipped_existing} already existed.`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 'success' && (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-500" />
                <div className="text-center">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">PDF downloaded successfully</p>
                  {generateResult && generateResult.created > 0 && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      {generateResult.created} new credential(s) were generated.
                    </p>
                  )}
                </div>
                <Button onClick={onClose} className="mt-2">
                  Done
                </Button>
              </div>
            )}

            {step === 'error' && (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-500" />
                <div className="text-center">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Export failed</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-sm">{error}</p>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" onClick={autoExportVendorEventIds ? onClose : () => setStep('select')}>
                    {autoExportVendorEventIds ? 'Close' : 'Back'}
                  </Button>
                  <Button onClick={autoExportVendorEventIds ? () => runExport(autoExportVendorEventIds) : handleExport}>
                    Retry
                  </Button>
                </div>
              </div>
            )}

            {/* Selection state */}
            {step === 'select' && (
              <>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  Select which vendors or locations to include in the login credentials PDF. Credentials will be generated automatically if they don't already exist.
                </p>

                {/* Tabs */}
                <div className="mb-4 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex space-x-6">
                    <button
                      onClick={() => setActiveTab('vendor_events')}
                      className={cn(
                        'px-1 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                        activeTab === 'vendor_events'
                          ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                          : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        Vendor Events
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('vendors')}
                      className={cn(
                        'px-1 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                        activeTab === 'vendors'
                          ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                          : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        <Store className="w-3.5 h-3.5" />
                        Vendors
                      </span>
                    </button>
                  </div>
                </div>

                {/* Search + Select all */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <Input
                      type="search"
                      placeholder={activeTab === 'vendor_events' ? 'Search locations...' : 'Search vendors...'}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <button
                    onClick={allSelected ? handleDeselectAll : handleSelectAll}
                    className="text-xs text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400 whitespace-nowrap font-medium px-2 py-1"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* Selection list */}
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg max-h-[320px] overflow-y-auto">
                  {activeTab === 'vendor_events' ? (
                    filteredVendorEvents.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                        No locations found.
                      </div>
                    ) : (
                      filteredVendorEvents.map((ve) => {
                        const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ''
                        const isSelected = selectedVendorEventIds.has(ve.id)
                        return (
                          <label
                            key={ve.id}
                            className={cn(
                              'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-b-0',
                              isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/10'
                                : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleVendorEvent(ve.id)}
                              className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                {ve.booth || 'Unnamed Location'}
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                                {vendorName}
                              </p>
                            </div>
                          </label>
                        )
                      })
                    )
                  ) : (
                    filteredVendors.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                        No vendors found.
                      </div>
                    ) : (
                      filteredVendors.map((vendor) => {
                        const isSelected = selectedVendorIds.has(vendor.id)
                        const locationCount = vendorToEventIds.get(vendor.id)?.length || 0
                        return (
                          <label
                            key={vendor.id}
                            className={cn(
                              'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-b-0',
                              isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/10'
                                : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleVendor(vendor.id)}
                              className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                {vendor.name}
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                {locationCount} location{locationCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </label>
                        )
                      })
                    )
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-800">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {selectionCount > 0
                      ? `${selectionCount} ${activeTab === 'vendor_events' ? 'location' : 'vendor'}${selectionCount !== 1 ? 's' : ''} selected (${derivedEventCount} location${derivedEventCount !== 1 ? 's' : ''} total)`
                      : 'No selection'}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleExport}
                      disabled={!hasSelection}
                      className="gap-2"
                    >
                      <KeyRound className="w-4 h-4" />
                      Export PDF
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
